import { SupplierContactType, SupplierType } from "@/interfaces";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export type SupplierLocation = { country?: string | null; city?: string | null };

/** Lists suppliers serving every selected location, for booking several items together. */
export const useSuppliersForLocationsQuery = (locations: SupplierLocation[]) => {
  const uniqueLocations = [...new Map(locations.filter((location) => location.country || location.city)
    .map(({ country, city }) => [JSON.stringify([country || "", city || ""]), { country, city }])).values()];
  return useQuery({
    queryKey: ["list-suppliers", "coverage", uniqueLocations],
    queryFn: async (): Promise<SupplierType[]> => {
      const results = await Promise.all((uniqueLocations.length ? uniqueLocations : [{}]).map(async (location: SupplierLocation) => {
        const params = new URLSearchParams({ isActive: "true" });
        if (location.country) params.set("country", location.country);
        if (location.city) params.set("city", location.city);
        return (await axios.get<SupplierType[]>(`/api/suppliers?${params}`)).data;
      }));
      return results[0].filter((supplier) => results.every((list) => list.some((entry) => entry.id === supplier.id)));
    },
  });
};

export const useGetSupplier = (id?: string) => {
  return useQuery({
    queryKey: ["supplier", id],
    queryFn: async (): Promise<SupplierType> => {
      const res = await axios.get(`/api/suppliers/${id}`);
      return res.data;
    },
    enabled: id ? true : false,
  });
};

/** Lists suppliers by destination coverage, optionally narrowing a country or state to a city. */
export const useListSuppliersQuery = (country?: string, isActive: boolean | null = true, city?: string) => {
  return useQuery({
    queryKey: ["list-suppliers", isActive, country, city],
    queryFn: async (): Promise<SupplierType[]> => {
      let queryParams = "";
      if (isActive !== null && isActive !== undefined) {
        queryParams += `isActive=${isActive}`;
      }
      if (country) {
        queryParams += queryParams ? `&country=${encodeURIComponent(country)}` : `country=${encodeURIComponent(country)}`;
      }
      if (city) {
        queryParams += queryParams ? `&city=${encodeURIComponent(city)}` : `city=${encodeURIComponent(city)}`;
      }

      const res = await axios.get(`/api/suppliers${queryParams ? "?" + queryParams : ""}`);
      return res.data;
    },
  });
};

type Pagination = { page: number; limit: number; total: number; totalPages: number };
type PaginatedSupplierContacts = { data: SupplierContactType[]; pagination: Pagination };
type SupplierContactPayload = Pick<SupplierContactType, "name" | "email" | "phone" | "region" | "isDefault">;

/** Fetches contacts for a selected supplier. */
export const useListSupplierContactsQuery = (supplierId?: string, params: Record<string, string | number | undefined> = {}) =>
  useQuery({
    queryKey: ["supplier-contacts", supplierId, params],
    queryFn: async (): Promise<PaginatedSupplierContacts> => {
      const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined).map(([key, value]) => [key, String(value)])).toString();
      return (await axios.get(`/api/suppliers/${supplierId}/contacts${query ? `?${query}` : ""}`)).data;
    },
    enabled: Boolean(supplierId),
  });

/** Creates a contact in a supplier directory. */
export const useCreateSupplierContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ supplierId, payload }: { supplierId: string; payload: SupplierContactPayload }) =>
      (await axios.post(`/api/suppliers/${supplierId}/contacts`, payload)).data as SupplierContactType,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["supplier-contacts", variables.supplierId] });
      queryClient.invalidateQueries({ queryKey: ["list-suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["supplier", variables.supplierId] });
    },
  });
};

/** Updates a supplier contact. */
export const useUpdateSupplierContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ supplierId, contactId, payload }: { supplierId: string; contactId: string; payload: Partial<SupplierContactPayload> }) =>
      (await axios.put(`/api/suppliers/${supplierId}/contacts/${contactId}`, payload)).data as SupplierContactType,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["supplier-contacts", variables.supplierId] });
      queryClient.invalidateQueries({ queryKey: ["list-suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["supplier", variables.supplierId] });
    },
  });
};

/** Removes a supplier contact and clears its optional record references. */
export const useDeleteSupplierContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ supplierId, contactId }: { supplierId: string; contactId: string }) =>
      axios.delete(`/api/suppliers/${supplierId}/contacts/${contactId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["supplier-contacts", variables.supplierId] });
      queryClient.invalidateQueries({ queryKey: ["list-suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["supplier", variables.supplierId] });
    },
  });
};

/** Lists every supplier contact with an email address for composing messages. */
export const useSupplierEmailRecipientsQuery = () =>
  useQuery({
    queryKey: ["supplier-email-recipients"],
    queryFn: async (): Promise<SupplierContactType[]> => (await axios.get("/api/suppliers/contact-recipients")).data,
  });
